import React, { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { hasMailPassword } from '../utils/mailCredentials';
import AlertModal from './AlertModal';
import ConfirmationModal from './ConfirmationModal';
import MailListRow from './MailListRow';
import MailReadingPane from './MailReadingPane';
import './Inbox.css';
import './FolderView.css';

const isImapMail = (mail) => mail && (mail.source === 'imap' || mail.uid);
const getMailUid = (mail) => mail?.uid ?? mail?.imapUid;
const getMailKey = (mail) => String(getMailUid(mail) ?? mail?._id ?? '');

/**
 * Archive — mail removed from the inbox but deliberately not deleted.
 *
 * Archived mail exists in two stores: the IMAP Archive mailbox and, for mail
 * delivered internally, `Mail` documents with `folder: 'archive'`. Both are
 * listed together so the folder looks like one place to the user, and neither
 * source failing hides the other.
 */
const Archive = ({ currentUser }) => {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showTrashConfirm, setShowTrashConfirm] = useState(false);

  const notify = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
  };

  const fetchArchive = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');

    const [mailbox, stored] = await Promise.allSettled([
      hasMailPassword() ? axiosInstance.get('/api/mail/server/archive') : Promise.resolve(null),
      axiosInstance.get('/api/mail/archive'),
    ]);

    const rowsOf = (outcome) =>
      outcome.status === 'fulfilled' && outcome.value?.data?.success
        ? outcome.value.data.data || []
        : [];

    const merged = [...rowsOf(mailbox), ...rowsOf(stored)].sort(
      (a, b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0)
    );

    setMails(merged);
    if (showLoading) setLoading(false);

    const failed = [mailbox, stored].filter((outcome) => outcome.status === 'rejected');
    if (!merged.length && failed.length) {
      setError(
        failed[0].reason?.response?.data?.message || 'Error loading archive. Please try again.'
      );
    }
  }, []);

  useEffect(() => {
    if (!currentUser) return undefined;
    fetchArchive();
    const interval = setInterval(() => fetchArchive(false), 30000);
    return () => clearInterval(interval);
  }, [currentUser, fetchArchive]);

  const handleSelectMail = (mailKey) => {
    setSelectedMailIds((prev) =>
      prev.includes(mailKey) ? prev.filter((id) => id !== mailKey) : [...prev, mailKey]
    );
  };

  const handleSelectAll = () => {
    setSelectedMailIds(selectedMailIds.length === mails.length ? [] : mails.map(getMailKey));
  };

  const removeLocally = (keys) => {
    const removed = new Set(keys.map(String));
    setMails((prev) => prev.filter((mail) => !removed.has(getMailKey(mail))));
    if (selectedMail && removed.has(getMailKey(selectedMail))) setSelectedMail(null);
    setSelectedMailIds((prev) => prev.filter((id) => !removed.has(id)));
    window.dispatchEvent(new CustomEvent('inboxUpdated'));
  };

  /** Move back to the inbox (or wherever it was archived from). */
  const unarchive = (mail) => {
    if (isImapMail(mail)) {
      return axiosInstance.patch(`/api/mail/unarchive/${getMailUid(mail)}`, {
        messageId: mail.messageId,
      });
    }
    return axiosInstance.patch(`/api/mail/unarchive/${mail._id}`);
  };

  const handleUnarchive = async (event, mail) => {
    event?.stopPropagation();
    try {
      await unarchive(mail);
      removeLocally([getMailKey(mail)]);
    } catch (err) {
      console.error('Error moving mail to inbox:', err);
      notify(err.response?.data?.message || 'Error moving mail to inbox. Please try again.');
    }
  };

  const handleBulkUnarchive = async () => {
    if (!selectedMailIds.length) return;
    const targets = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
    const outcomes = await Promise.allSettled(targets.map(unarchive));

    const movedKeys = targets
      .filter((_, index) => outcomes[index].status === 'fulfilled')
      .map(getMailKey);
    removeLocally(movedKeys);

    const failures = outcomes.filter((outcome) => outcome.status === 'rejected').length;
    if (failures) notify(`${failures} mail(s) could not be moved to the inbox.`);
  };

  const confirmMoveToTrash = async () => {
    setShowTrashConfirm(false);
    const targets = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
    const outcomes = await Promise.allSettled(
      targets.map((mail) =>
        isImapMail(mail)
          ? axiosInstance.patch(`/api/mail/trash/${getMailUid(mail)}`)
          : axiosInstance.patch(`/api/mail/move-to-trash/${mail._id}`)
      )
    );

    removeLocally(
      targets.filter((_, index) => outcomes[index].status === 'fulfilled').map(getMailKey)
    );

    const failures = outcomes.filter((outcome) => outcome.status === 'rejected').length;
    if (failures) notify(`${failures} mail(s) could not be moved to trash.`);
  };

  const handleMailClick = async (mail) => {
    setSelectedMail(mail);
    if (!isImapMail(mail)) return;

    // The list fetch is deliberately light (envelopes only), so pull the body
    // on open.
    try {
      const detail = await axiosInstance.get(`/api/mail/server/message/${getMailUid(mail)}`);
      if (detail.data.success) setSelectedMail(detail.data.data);
    } catch (err) {
      console.error('Error loading archived message:', err);
    }
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading archive...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button onClick={() => fetchArchive()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`inbox-container premium-mail-dashboard ${selectedMail ? 'mail-detail-open' : ''}`}>
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">ATOZASMAIL Workspace</p>
          <h2>Archive</h2>
        </div>
        <div className="inbox-header-actions">
          <p className="mail-count">
            {mails.length} {mails.length === 1 ? 'mail' : 'mails'}
          </p>
        </div>
      </div>

      <div
        className={`inbox-content reading-pane-no-split gmail-split-layout${
          selectedMail ? ' has-detail-open full-detail-view' : ''
        }`}
      >
        <div className={`mail-list-container ${selectedMail ? 'hidden' : 'full-width'}`}>
          {mails.length > 0 && (
            <div className="mail-toolbar">
              <div className="toolbar-left">
                <button
                  className="toolbar-btn checkbox-btn"
                  onClick={handleSelectAll}
                  title={selectedMailIds.length ? 'Deselect all' : 'Select all'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    {selectedMailIds.length > 0 && <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round" />}
                  </svg>
                </button>

                <button className="toolbar-btn" onClick={() => fetchArchive()} title="Refresh">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6" />
                    <path d="M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                </button>

                {selectedMailIds.length > 0 && (
                  <>
                    <button className="toolbar-btn" onClick={handleBulkUnarchive} title="Move to inbox">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-7-3l4-4h-3V8h-2v4H8l4 4z" transform="rotate(180 12 12)" />
                      </svg>
                    </button>

                    <button
                      className="toolbar-btn"
                      onClick={() => setShowTrashConfirm(true)}
                      title="Move to trash"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="mail-list">
            {mails.length === 0 ? (
              <div className="empty-inbox">
                <p>Nothing archived yet</p>
                <p className="empty-hint">
                  Archiving removes mail from your inbox without deleting it. It stays searchable
                  here and in All Mail.
                </p>
              </div>
            ) : (
              mails.map((mail) => {
                const mailKey = getMailKey(mail);
                return (
                  <MailListRow
                    key={mailKey}
                    mail={mail}
                    mailKey={mailKey}
                    currentUserEmail={currentUser?.email}
                    isChecked={selectedMailIds.includes(mailKey)}
                    isOpen={selectedMail && getMailKey(selectedMail) === mailKey}
                    isUnread={!mail.isRead && !mail.seen}
                    onOpen={handleMailClick}
                    onToggleCheck={handleSelectMail}
                    actions={[
                      {
                        key: 'unarchive',
                        title: 'Move to inbox',
                        icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 15v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4h2v4h12v-4h2zM12 3l5 5h-3v6h-4V8H7l5-5z" />
                          </svg>
                        ),
                        onClick: handleUnarchive,
                      },
                    ]}
                  />
                );
              })
            )}
          </div>
        </div>

        {selectedMail && (
          <MailReadingPane
            mail={selectedMail}
            folderLabel="Archive"
            onClose={() => setSelectedMail(null)}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showTrashConfirm}
        onClose={() => setShowTrashConfirm(false)}
        onConfirm={confirmMoveToTrash}
        message={`Move ${selectedMailIds.length} archived mail(s) to trash?`}
        confirmText="Move to trash"
        cancelText="Cancel"
      />

      <AlertModal isOpen={showAlert} onClose={() => setShowAlert(false)} message={alertMessage} />
    </div>
  );
};

export default Archive;
